
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { X, Eye } from "lucide-react";
import { Separator } from "@/components/ui/separator";

interface GuestInfo {
  invite_id: string;
  display_name: string;
  phone_number?: string;
  rsvp_status: 'invited' | 'accepted' | 'declined' | 'maybe';
  feedback_text?: string | null;
  suggested_venue?: string | null;
  suggested_time?: string | null;
  claimed_by_user_id?: string | null;
  user_phone_number?: string | null;
  selected_dates?: string[] | null;
  selected_times?: string[] | null;
  avatar_url?: string | null;
  user_full_name?: string | null;
  guest_avatar_url?: string | null;
}

interface GuestListItemProps {
  guest: GuestInfo;
  isHost: boolean;
  onViewFeedback: () => void;
  onRemoveGuest?: () => void;
  hasFeedback: boolean;
}

export const GuestListItem = ({ 
  guest, 
  isHost, 
  onViewFeedback, 
  onRemoveGuest,
  hasFeedback
}: GuestListItemProps) => {
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(part => part.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2) || 'G';
  };

  const getAvatarClassForStatus = (status: 'invited' | 'accepted' | 'declined' | 'maybe') => {
    switch(status) {
      case 'accepted':
        return "border-2 border-white";
      case 'declined':
        return "border-2 border-black";
      default:
        return "border-2 border-black";
    }
  };
  
  const getFallbackClassForStatus = (status: 'invited' | 'accepted' | 'declined' | 'maybe') => {
    switch(status) {
      case 'accepted':
        return "!bg-black text-white"; // Attending: black fill, white text
      case 'declined':
        return "!bg-gray-200 text-black"; // Not attending: lighter grey fill, black text
      default:
        return "!bg-white text-black"; // Yet to respond: white fill, black text
    }
  };

  const displayPhoneNumber = guest.claimed_by_user_id 
    ? guest.user_phone_number 
    : guest.phone_number;

  const showUserFullName = guest.user_full_name && 
                          guest.user_full_name !== guest.display_name;
  
  const canRemoveGuest = guest.rsvp_status === 'invited';

  return (
    <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
      <div className="flex items-center gap-3">
        <Avatar className={`h-10 w-10 ${getAvatarClassForStatus(guest.rsvp_status)}`}>
          {guest.guest_avatar_url ? (
            <AvatarImage 
              src={guest.guest_avatar_url} 
              alt={guest.display_name} 
              className="object-cover object-center" 
            />
          ) : guest.avatar_url ? (
            <AvatarImage 
              src={guest.avatar_url} 
              alt={guest.display_name}
              className="object-cover object-center"
            />
          ) : null}
          <AvatarFallback className={getFallbackClassForStatus(guest.rsvp_status)}>
            {getInitials(guest.display_name)}
          </AvatarFallback>
        </Avatar>
        <div>
          <p className="font-medium">{guest.display_name}</p>
          
          {isHost && (
            <div className="text-sm text-gray-500">
              {showUserFullName && (
                <span>{guest.user_full_name}</span>
              )}
              
              {showUserFullName && displayPhoneNumber && (
                <span className="mx-1 inline-flex items-center">
                  <span className="h-1 w-1 rounded-full bg-gray-400 mx-1"></span>
                </span>
              )}
              
              {displayPhoneNumber && (
                <span>{displayPhoneNumber}</span>
              )}
            </div>
          )}
          
          {!isHost && showUserFullName && (
            <p className="text-sm text-gray-500">{guest.user_full_name}</p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        {hasFeedback && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onViewFeedback}
            className="text-gray-500 hover:text-primary"
            aria-label="View feedback"
          >
            <Eye className="h-4 w-4" />
          </Button>
        )}
        {isHost && onRemoveGuest && canRemoveGuest && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onRemoveGuest}
            className="text-gray-500 hover:text-red-500"
            aria-label="Remove guest"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
};
